// Session cookie helpers shared by client and server.
//
// The auth token is obtained client-side from the Nostr remote-signer handshake,
// so the cookie is written from the browser. It is intentionally NOT HttpOnly:
// the same value is read both here (client) and by SvelteKit server `load`
// functions via `cookies.get()` — the browser keeps sending it with navigations.
//
// This module is pure (no runes, no module-level state) so it is safe to import
// from server code. Reactive client state lives in `sessionStore.svelte.ts`.

import { browser } from '$app/environment';
import { DEFAULT_ADMIN_API_BASE, adminApiBase } from '$lib/apiHost.svelte';
import type { User } from '$lib/api/adminAPI';
import type { LoginType } from '$lib/nostr/types';

export const SESSION_COOKIE = 'tsunami_session';

// Tokens are issued by — and only valid on — one API server, so each server
// gets its own session cookie: switching hosts (header → Network Settings)
// swaps to that host's session instead of destroying the current one, and
// switching back restores it. The default server keeps the bare cookie name so
// existing sessions survive this change.
export const sessionCookieName = (base: string = adminApiBase()): string => {
  if (base === DEFAULT_ADMIN_API_BASE) return SESSION_COOKIE;
  return `${SESSION_COOKIE}_${base.replace(/^https?:\/\//, '').replace(/[^a-zA-Z0-9]+/g, '_')}`;
};

export type Session = {
  token: string;
  pubkey: string;
  loginType: LoginType;
  // Unix timestamp (seconds) at which the auth token expires.
  expiresAt: number;
  // The authenticated user's admin-API record, fetched at login. Optional
  // because it may be unavailable (e.g. the user lacks `manage_admin_users`).
  currentUser?: User | null;
  // The API server this token was issued by. The cookie *name* only carries a
  // lossy slug of it, so the base URL is stored here too — that's what lets
  // `listSignedInServers()` offer a switch back. Absent on sessions written
  // before sessions became per-server; those can only be the default server.
  apiBase?: string;
};

export const serializeSession = (session: Session): string =>
  encodeURIComponent(JSON.stringify(session));

const safeDecode = (value: string): string | null => {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
};

// Parse a cookie value into a Session. Tolerates both raw and percent-encoded
// input because SvelteKit's `cookies.get()` already URL-decodes on the server
// while `document.cookie` returns the stored (encoded) value on the client.
export const parseSession = (raw: string | undefined | null): Session | null => {
  if (!raw) return null;

  for (const candidate of [raw, safeDecode(raw)]) {
    if (!candidate) continue;
    try {
      const decoded = JSON.parse(candidate) as Partial<Session>;
      if (
        typeof decoded.token === 'string' &&
        typeof decoded.pubkey === 'string' &&
        typeof decoded.loginType === 'string' &&
        typeof decoded.expiresAt === 'number'
      ) {
        return decoded as Session;
      }
    } catch {
      // try the next candidate
    }
  }

  return null;
};

// A session is valid while its token has not yet expired.
export const isSessionValid = (session: Session | null): session is Session =>
  !!session && session.expiresAt * 1000 > Date.now();

// Seconds until the token expires (never negative).
export const secondsUntilExpiry = (session: Session): number =>
  Math.max(0, session.expiresAt - Math.floor(Date.now() / 1000));

// --- Client-side cookie access (document.cookie) ---------------------------

// All three default to the *active* API server's cookie, so callers (the route
// guards, `sessionStore`) stay server-agnostic.

export const readSessionCookie = (base?: string): Session | null => {
  if (!browser) return null;

  const name = sessionCookieName(base);
  const entry = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));

  if (!entry) return null;

  return parseSession(entry.slice(name.length + 1));
};

export const writeSessionCookie = (session: Session, base?: string) => {
  if (!browser) return;

  const maxAge = secondsUntilExpiry(session);
  document.cookie = `${sessionCookieName(base)}=${serializeSession(session)}; path=/; max-age=${maxAge}; SameSite=Lax`;
};

export const deleteSessionCookie = (base?: string) => {
  if (!browser) return;

  document.cookie = `${sessionCookieName(base)}=; path=/; max-age=0; SameSite=Lax`;
};

// Every server this browser still holds a valid (unexpired) session for, sorted
// by URL — the Network dialog lists them so the user can switch back with one
// click instead of re-typing a URL and signing in again.
export const listSignedInServers = (): { base: string; session: Session }[] => {
  if (!browser) return [];

  const found = new Map<string, Session>();

  for (const entry of document.cookie.split('; ')) {
    const eq = entry.indexOf('=');
    if (eq < 0) continue;

    const name = entry.slice(0, eq);
    if (name !== SESSION_COOKIE && !name.startsWith(`${SESSION_COOKIE}_`)) continue;

    const session = parseSession(entry.slice(eq + 1));
    if (!isSessionValid(session)) continue;

    const base = session.apiBase ?? DEFAULT_ADMIN_API_BASE;
    if (!found.has(base)) found.set(base, session);
  }

  return [...found]
    .map(([base, session]) => ({ base, session }))
    .sort((a, b) => a.base.localeCompare(b.base));
};

// Drop the session(s) for one server, so it leaves `listSignedInServers()`.
//
// Deliberately name-agnostic: it scans the session cookies and deletes the ones
// whose *resolved* base matches, rather than deleting `sessionCookieName(base)`.
// The two can differ — a cookie written before sessions became per-server has no
// `apiBase`, so it resolves to the default server whatever its own name slug
// says — and deleting the derived name would then silently delete nothing.
export const forgetServerSession = (base: string) => {
  if (!browser) return;

  for (const entry of document.cookie.split('; ')) {
    const eq = entry.indexOf('=');
    if (eq < 0) continue;

    const name = entry.slice(0, eq);
    if (name !== SESSION_COOKIE && !name.startsWith(`${SESSION_COOKIE}_`)) continue;

    const session = parseSession(entry.slice(eq + 1));
    if ((session?.apiBase ?? DEFAULT_ADMIN_API_BASE) !== base) continue;

    document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
  }
};

// Drop every server's session, not just the active one — an explicit logout
// must not leave a usable token behind for the next person on this browser.
export const deleteAllSessionCookies = () => {
  if (!browser) return;

  for (const entry of document.cookie.split('; ')) {
    const name = entry.split('=')[0];
    if (name === SESSION_COOKIE || name.startsWith(`${SESSION_COOKIE}_`)) {
      document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
    }
  }
};
